import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { initiativeCategory as initiativeCategorySchema } from '../lib/schema.js';
import { selectCurrentOrganizationId } from './auth.js';
import { deleteEntities, selectEntities } from './entities.js';

const RETRIEVE_CATEGORIES_REQUEST =
  'changesync/initiativeCategories/RETRIEVE_CATEGORIES_REQUEST';
const RETRIEVE_CATEGORIES_SUCCESS =
  'changesync/initiativeCategories/RETRIEVE_CATEGORIES_SUCCESS';
const RETRIEVE_CATEGORIES_FAILURE =
  'changesync/initiativeCategories/RETRIEVE_CATEGORIES_FAILURE';
const CREATE_CATEGORY_REQUEST =
  'changesync/initiativeCategories/CREATE_CATEGORY_REQUEST';
const CREATE_CATEGORY_SUCCESS =
  'changesync/initiativeCategories/CREATE_CATEGORY_SUCCESS';
const CREATE_CATEGORY_FAILURE =
  'changesync/initiativeCategories/CREATE_CATEGORY_FAILURE';
const UPDATE_CATEGORIES_REQUEST =
  'changesync/initiativeCategories/UPDATE_CATEGORIES_REQUEST';
const UPDATE_CATEGORIES_SUCCESS =
  'changesync/initiativeCategories/UPDATE_CATEGORIES_SUCCESS';
const UPDATE_CATEGORIES_FAILURE =
  'changesync/initiativeCategories/UPDATE_CATEGORIES_FAILURE';
const DELETE_CATEGORY_REQUEST =
  'changesync/initiativeCategories/DELETE_CATEGORY_REQUEST';
const DELETE_CATEGORY_SUCCESS =
  'changesync/initiativeCategories/DELETE_CATEGORY_SUCCESS';
const DELETE_CATEGORY_FAILURE =
  'changesync/initiativeCategories/DELETE_CATEGORY_FAILURE';

const initialState = {
  retrieveInitiativeCategories: {
    initiativeCategoriesIds: [],
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_CATEGORIES_REQUEST:
      return {
        ...state,
        retrieveInitiativeCategories: {
          ...state.retrieveInitiativeCategories,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_CATEGORIES_SUCCESS:
      return {
        ...state,
        retrieveInitiativeCategories: {
          initiativeCategoriesIds: action.payload.result,
          pending: false,
          success: true,
          error: null,
        },
      };

    case RETRIEVE_CATEGORIES_FAILURE:
      return {
        ...state,
        retrieveInitiativeCategories: {
          ...state.retrieveInitiativeCategories,
          pending: false,
          error: action.error,
        },
      };

    default:
      return state;
  }
};

// Action Creators

export const retrieveInitiativeCategories =
  (initiativeType) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());

    const endpoint = `organizations/${organizationId}/initiative_categories`;
    const query = `?initiative_type=${initiativeType}`;

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_CATEGORIES_REQUEST,
          RETRIEVE_CATEGORIES_SUCCESS,
          RETRIEVE_CATEGORIES_FAILURE,
        ],
        endpoint: initiativeType ? endpoint + query : endpoint,
        method: HTTP_METHODS.GET,
        schema: [initiativeCategorySchema],
      },
    });
  };

export const createInitiativeCategory = (category) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        CREATE_CATEGORY_REQUEST,
        CREATE_CATEGORY_SUCCESS,
        CREATE_CATEGORY_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/initiative_categories`,
      method: HTTP_METHODS.POST,
      schema: initiativeCategorySchema,
      body: { category },
    },
  });
};

export const updateInitiativeCategory = (categoryId, category) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        UPDATE_CATEGORIES_REQUEST,
        UPDATE_CATEGORIES_SUCCESS,
        UPDATE_CATEGORIES_FAILURE,
      ],
      endpoint: `initiative_categories/${categoryId}`,
      method: HTTP_METHODS.PUT,
      body: { category },
    },
  });

export const updateInitiativeCategories =
  (categories) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());

    const endpoint = `organizations/${organizationId}/initiative_categories/bulk_update`;
    return dispatch({
      [CALL_API]: {
        types: [
          UPDATE_CATEGORIES_REQUEST,
          UPDATE_CATEGORIES_SUCCESS,
          UPDATE_CATEGORIES_FAILURE,
        ],
        endpoint,
        method: HTTP_METHODS.PUT,
        body: categories,
      },
    });
  };

export const deleteInitiativeCategory = (id) => async (dispatch) => {
  await dispatch({
    [CALL_API]: {
      types: [
        DELETE_CATEGORY_REQUEST,
        DELETE_CATEGORY_SUCCESS,
        DELETE_CATEGORY_FAILURE,
      ],
      endpoint: `initiative_categories/${id}`,
      method: HTTP_METHODS.DELETE,
    },
  });

  return dispatch(deleteEntities([`initiativeCategories.${id}`]));
};

export const selectRetrieveInitiativeCategoriesState = (state) =>
  state.initiativeCategories.retrieveInitiativeCategories;

const selectInitiativeCategoryIds = (state) =>
  state.initiativeCategories.retrieveInitiativeCategories
    .initiativeCategoriesIds;

export const selectInitiativeCategories = createSelector(
  selectInitiativeCategoryIds,
  selectEntities,
  (initiativeCategoriesIds, entities) => {
    return denormalize(
      initiativeCategoriesIds,
      [initiativeCategorySchema],
      entities
    ).filter(({ archived }) => !archived);
  }
);

export const selectInitiativeCategoriesWithArchived = createSelector(
  selectInitiativeCategoryIds,
  selectEntities,
  (initiativeCategoriesIds, entities) =>
    denormalize(initiativeCategoriesIds, [initiativeCategorySchema], entities)
);
